import React, { useMemo } from 'react'
import { connect, ConnectedProps } from 'react-redux'

import { SelectOption } from 'controlled-components'
import {
  clearBottomSelection,
  closeTop,
  filterOptionsBySearch,
  selectTopData,
  selectTopSearchTerm,
  selectTopSelectedItem,
  setBottomDisabled,
  // Bottom actions for cascading business logic
  setTopSearchTerm,
  setTopSelectedItem,
} from 'slices/aiSegmentBuilder/picker'

// ═══════════════════════════════════════════════════════════════════════════
// Types
// ═══════════════════════════════════════════════════════════════════════════

interface OwnProps {
  pickerId: number
}

type Props = OwnProps & ConnectedProps<typeof connector>

interface DataItem {
  id: string
  name: string
}

// ═══════════════════════════════════════════════════════════════════════════
// OptionItem Component
// ═══════════════════════════════════════════════════════════════════════════

interface OptionItemProps {
  item: DataItem
  isSelected: boolean
  onSelect: (key: string) => void
}

const OptionItem = React.memo(
  ({ item, isSelected, onSelect }: OptionItemProps) => {
    const handleSelect = () => onSelect(item.id)

    return (
      <SelectOption
        key={item.id}
        value={item.id}
        selected={isSelected}
        onSelect={handleSelect}
      >
        {item.name}
      </SelectOption>
    )
  }
)

// ═══════════════════════════════════════════════════════════════════════════
// Component
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Container for the options list in the top selector dropdown.
 * Renders options with selection handling.
 */
const OptionsListContainer: React.FC<Props> = ({
  pickerId,
  data,
  searchTerm,
  selectedItem,
  setSelected,
  setSearchTerm,
  close,
  clearBottom,
  setBottomDis,
}) => {
  // Visible options, filtered client-side via the shared helper (single source
  // of truth shared with the multi-select header/options list).
  const options: DataItem[] = useMemo(
    () => filterOptionsBySearch(data, searchTerm),
    [data, searchTerm]
  )

  // Handler for selecting an option
  const handleSelect = (key: string) => {
    setSelected({ pickerId, key })
    // Reset search term and close dropdown
    setSearchTerm({ pickerId, term: '' })
    close({ pickerId })
    // Business logic: clear bottom selection when top changes, then enable bottom
    clearBottom({ pickerId })
    setBottomDis({ pickerId, disabled: false })
  }

  return (
    <>
      {options.map(item => (
        <OptionItem
          key={item.id}
          item={item}
          isSelected={selectedItem === item.id}
          onSelect={handleSelect}
        />
      ))}
    </>
  )
}

// ═══════════════════════════════════════════════════════════════════════════
// Redux Connection
// ═══════════════════════════════════════════════════════════════════════════

const mapStateToProps = (state: any, ownProps: OwnProps) => ({
  data: selectTopData(state, ownProps.pickerId),
  searchTerm: selectTopSearchTerm(state, ownProps.pickerId),
  selectedItem: selectTopSelectedItem(state, ownProps.pickerId),
})

const mapDispatchToProps = {
  setSelected: setTopSelectedItem,
  setSearchTerm: setTopSearchTerm,
  close: closeTop,
  // Bottom actions for cascading business logic
  clearBottom: clearBottomSelection,
  setBottomDis: setBottomDisabled,
}

const connector = connect(mapStateToProps, mapDispatchToProps)

export default connector(OptionsListContainer)
